import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import { formatRole } from '../utils/roleLabel';
import './UniversityAdminDashboard.css';

const UniversityAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalColleges: 0,
    activeColleges: 0,
    totalUsers: 0,
    certifiedFaculty: 0,
    eligibleFaculty: 0,
    activePoolPapers: 0,
    contributingExaminers: 0,
    smeReviews: 0,
    generatedSets: 0,
    pendingCOEReview: 0
  });
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState('');

  const fetchColleges = useCallback(async () => {
    try {
      const { data } = await API.get(`/public/colleges?university_id=${user.university_id}`);
      setColleges(data.colleges || []);
    } catch (err) {
      console.error('Failed to fetch colleges');
    }
  }, [user.university_id]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const { data } = await API.get('/auth/profile/stats');
      setStats(prev => ({ ...prev, ...data.stats }));
    } catch (err) {
      console.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCertificationStats = useCallback(async () => {
    try {
      const [eligibleRes, certifiedRes] = await Promise.all([
        API.get('/faculty-certification/eligible-faculty'),
        API.get('/faculty-certification/certified-faculty')
      ]);

      setStats(prev => ({
        ...prev,
        eligibleFaculty: eligibleRes.data.count || 0,
        certifiedFaculty: certifiedRes.data.count || 0
      }));
    } catch (err) {
      console.error('Failed to fetch certification stats');
    }
  }, []);

  const fetchUniversityPoolStats = useCallback(async () => {
    try {
      const { data: poolsData } = await API.get('/university-pool/pool-papers');
      const pools = poolsData.pool_papers || [];

      const { data: coeData } = await API.get('/university-pool-coe/pools-ready');
      const pendingCOE = coeData.pools || [];

      setStats(prev => ({
        ...prev,
        activePoolPapers: pools.filter(p => p.status === 'active').length,
        pendingCOEReview: pendingCOE.length,
        contributingExaminers: 0,
        smeReviews: 0,
        generatedSets: 0
      }));
    } catch (err) {
      console.error('Failed to fetch university pool stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchColleges();
    fetchCertificationStats();
    fetchUniversityPoolStats();
  }, [fetchDashboardData, fetchColleges, fetchCertificationStats, fetchUniversityPoolStats]);

  if (loading) {
    return (
      <div className="ua-spinner-wrap">
        <div className="ua-spinner"></div>
      </div>
    );
  }

  return (
    <div className="ua-page">
      <div className="ua-page-header">
        <h1>University Admin Dashboard</h1>
        <p>Welcome back, {user.name} ({formatRole(user.role)})! Manage university-wide colleges, registrations, and subjects.</p>
      </div>

      {/* Stats Cards */}
      <div className="ua-stats-grid">
        <div className="ua-stat-card" onClick={() => navigate('/university-admin/college-management')} style={{ cursor: 'pointer' }}>
          <div className="ua-stat-icon ua-stat-icon--blue"><Icon name="Building" size={24} /></div>
          <div>
            <div className="ua-stat-value">{stats.totalColleges || colleges.length}</div>
            <div className="ua-stat-label">Total Colleges</div>
          </div>
        </div>
        <div className="ua-stat-card" onClick={() => navigate('/university-admin/college-management')} style={{ cursor: 'pointer' }}>
          <div className="ua-stat-icon ua-stat-icon--green"><Icon name="CheckCircle" size={24} /></div>
          <div>
            <div className="ua-stat-value">{stats.activeColleges || colleges.filter(c => c.status === 'active').length}</div>
            <div className="ua-stat-label">Active Colleges</div>
          </div>
        </div>
        <div className="ua-stat-card" onClick={() => navigate('/university-admin/user-management')} style={{ cursor: 'pointer' }}>
          <div className="ua-stat-icon ua-stat-icon--purple"><Icon name="Users" size={24} /></div>
          <div>
            <div className="ua-stat-value">{stats.totalUsers || 0}</div>
            <div className="ua-stat-label">Total Users</div>
          </div>
        </div>
        <div className="ua-stat-card" onClick={() => navigate('/university-admin/faculty-certification')} style={{ cursor: 'pointer' }}>
          <div className="ua-stat-icon ua-stat-icon--indigo"><Icon name="GraduationCap" size={24} /></div>
          <div>
            <div className="ua-stat-value">{stats.certifiedFaculty}</div>
            <div className="ua-stat-label">Certified Faculty</div>
          </div>
        </div>
        <div className="ua-stat-card" onClick={() => navigate('/university-admin/faculty-certification')} style={{ cursor: 'pointer' }}>
          <div className="ua-stat-icon ua-stat-icon--rose"><Icon name="Clipboard" size={24} /></div>
          <div>
            <div className="ua-stat-value">{stats.eligibleFaculty}</div>
            <div className="ua-stat-label">Eligible Faculty</div>
          </div>
        </div>
      </div>

      {error && <div className="ua-alert ua-alert--error">{error}</div>}

      {/* Quick Actions */}
      <div className="ua-card">
        <div className="ua-card-header">
          <h2>Quick Actions</h2>
        </div>
        <div style={{ padding: '1.25rem' }}>
          <div className="ua-quick-actions-grid">

            <div className="ua-action-card" onClick={() => navigate('/university-admin/college-management')}>
              <div className="ua-action-icon" style={{ background: '#fef3c7', color: '#d97706' }}><Icon name="Building" size={20} /></div>
              <div>
                <div className="ua-action-title">Manage Colleges</div>
                <div className="ua-action-desc">Register and monitor affiliated college institutes.</div>
              </div>
            </div>

            <div className="ua-action-card" onClick={() => navigate('/university-admin/user-management')}>
              <div className="ua-action-icon" style={{ background: '#faf5ff', color: '#7c3aed' }}><Icon name="Users" size={20} /></div>
              <div>
                <div className="ua-action-title">Manage Users</div>
                <div className="ua-action-desc">View and manage university users and their roles.</div>
              </div>
            </div>

            <div className="ua-action-card" onClick={() => navigate('/university-admin/subject-management')}>
              <div className="ua-action-icon" style={{ background: '#eff6ff', color: '#2563eb' }}><Icon name="Book" size={20} /></div>
              <div>
                <div className="ua-action-title">Manage Subjects</div>
                <div className="ua-action-desc">Add university subjects and assign curriculum details.</div>
              </div>
            </div>

            <div className="ua-action-card" onClick={() => navigate('/university-admin/faculty-certification')}>
              <div className="ua-action-icon" style={{ background: '#ecfdf5', color: '#059669' }}><Icon name="GraduationCap" size={20} /></div>
              <div>
                <div className="ua-action-title">Faculty Certification</div>
                <div className="ua-action-desc">Certify eligible college examiners for question generation.</div>
              </div>
            </div>

            <div className="ua-action-card" onClick={() => navigate('/university-admin/templates?tab=create')}>
              <div className="ua-action-icon" style={{ background: '#faf5ff', color: '#7c3aed' }}><Icon name="Clipboard" size={20} /></div>
              <div>
                <div className="ua-action-title">Create Template</div>
                <div className="ua-action-desc">Design a new question paper blueprint.</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityAdminDashboard;